import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';

export const leaveBalanceKeys = {
    all: ['leave-balances'],
    list: (orgId, employeeId) => [...leaveBalanceKeys.all, orgId, employeeId || 'all'],
    history: (orgId) => [...leaveBalanceKeys.all, 'history', orgId],
};

// Fetch all leave balances (for Admin/HR)
// export function useLeaveBalances(orgId) {
//     return useQuery({
//         queryKey: leaveBalanceKeys.list(orgId),
//         queryFn: async () => {
//             const response = await apiHelper.getResource(`${url}leave/balances/});
//             return response.data;
//         },
//         enabled: !!orgId,
//     );
// }

// Fetch specific employee leave balance
// export function useEmployeeLeaveBalance(employeeId) {
//     return useQuery({
//         queryKey: leaveBalanceKeys.employee(employeeId),
//         queryFn: async () => {
//             // Endpoint might vary, assuming /leave/balances/{employeeId}/ or filtered list
//             // If backend only has one endpoint that returns list or single based on user role, 
//             // we might need to adjust. For now, assuming specific endpoint or using list with filter.
//             // A common pattern is /leave/balances/me/ for current user or /leave/balances/?employee_id=X
//             // Let's assume /leave/balances/{id}/ works for getting specific balance
//             const response = await apiHelper.getResource(`${url}leave/balances/${employeeId}/});
//             return response.data;
//         },
//         enabled: !!employeeId,
//     });
// }

// Fetch current user's leave balance
// In your hooks file
// Add a check to ensure you aren't passing "undefined" strings
// hooks/useLeaveBalance.js
// hooks/useLeaveBalance.js
export function useLeaveBalances(orgId, employeeId = null, options = {}) {
    return useQuery({
        queryKey: leaveBalanceKeys.list(orgId, employeeId),
        queryFn: async () => {
            try {
                const params = {};
                if (employeeId) params.employee = employeeId;

                // We pass orgId inside the config object so your API utility 
                // can inject it into the headers automatically.
                const response = await apiHelper.getResource('/leave/balances/', {
                    params,
                    orgId
                });

                // ALWAYS return an array, even if the backend returns null
                return Array.isArray(response) ? response : (response?.results || []);
            } catch (err) {
                console.error("Fetch Leave Balances Error:", err);
                return []; // Return empty array on error to prevent "data is undefined"
            }
        },
        enabled: !!orgId,
        ...options
    });
}

// HR: Adjust a single employee's balance
export function useAdjustEmployeeBalance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) =>
            apiHelper.postResource('/leave/balances/adjust-employee/', data, orgId ? { orgId } : {}),
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveBalanceKeys.list(orgId) });
            queryClient.invalidateQueries({ queryKey: leaveBalanceKeys.history(orgId) });
        },
    });
}

// HR: Fetch adjustment history (with optional filters)
export function useAdjustmentHistory(orgId, filters = {}) {
    return useQuery({
        queryKey: [...leaveBalanceKeys.history(orgId), filters],
        queryFn: () =>
            apiHelper.getResource('/leave/balances/adjustment-history/', { orgId, params: filters }),
        enabled: !!orgId,
    });
}

// HR: Bulk set balance for all employees
export function useBulkSetBalance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) =>
            apiHelper.postResource('/leave/balances/bulk-set/', data, orgId ? { orgId } : {}),
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveBalanceKeys.list(orgId) });
        },
    });
}

// HR: Seed missing balance rows
export function useSeedMissingBalances() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ orgId }) =>
            apiHelper.postResource('/leave/balances/seed-missing/', {}, orgId ? { orgId } : {}),
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveBalanceKeys.list(orgId) });
        },
    });
}